<script lang="ts">
  import { Card, Button, Checkbox, Input, Helper } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faSave, faTimes, faAsterisk } from "@fortawesome/free-solid-svg-icons";
  import { createEventDispatcher } from "svelte";

  import { onMount } from "svelte";
  import { getUserFromAzureAD, getUserByOID } from "../../services/api/data-handler.js";
  import { userStore } from "../../services/stores/user-store.js";

  interface UserData {
    user: {
      user_id: string;
      name: string;
      email: string;
      oid: string;
      user_type: string;
      user_role: string;
      claims: Record<string, boolean>;
      metadata: Record<string, any>;
      customers: any[];
    };
    lookups: {
      customer: {
        list: Array<{
          customer_id: string;
          name: string;
          status: string;
        }>;
      };
      user_type: {
        list: Array<{
          id: string;
          name: string;
          role: {
            list: Array<{
              id: string;
              name: string;
              claims: {
                canViewResults: boolean;
                canManageProjects: boolean;
                canManageCustomers: boolean;
                canManageUsers: boolean;
                canLoadProjectDocuments: boolean;
              };
              "customer-relationship": "all" | "multiple" | "single";
            }>;
          };
        }>;
      };
    };
  }

  type ValidFields = "oid" | "user_type" | "user_role";

  export let object: UserData;
  export let action: "add" | "edit";
  export let isEditMode = false;

  let userInput: typeof object.user;
  let lookups = object?.lookups || {
    customer: { list: [] },
    user_type: { list: [] }
  };
  let userExists = false;

  let initialized = false;
  $: if (object && !initialized) {
    initialized = true;
    userInput = { ...object.user };
    lookups = object.lookups;
    validateAllFields(); // Initial validation
  }

  const dispatch = createEventDispatcher();

  // Define required fields - removed name and email since they're readonly
  const requiredFields: Set<ValidFields> = new Set(['oid', 'user_type', 'user_role']);

  let errors: Record<ValidFields, string> = {
    oid: "",
    user_type: "",
    user_role: ""
  };

  $: errorState = errors;
  $: hasErrors = Object.values(errorState).some((error) => error !== "");

  function validateField(field: ValidFields, value: any) {
    if (field === 'oid') {
      if (!value?.trim()) {
        errors[field] = "OID is required";
      } else if (!isValidUUID(value)) {
        errors[field] = "Invalid OID format";
      } else if (userExists) {
        errors[field] = "User already exists in the system";
      } else if (apiError) {
        errors[field] = apiError;
      } else if (userInput.name && userInput.email) {
        errors[field] = ""; // Only valid when everything is correct
      } else {
        errors[field] = "Invalid user";
      }
    } else {
      // For user_type and user_role
      errors[field] = !value?.trim() ? `${field} is required` : "";
    }
    errorState = { ...errors };
  }

  function validateAllFields() {
    if (!userInput) return;
    
    requiredFields.forEach(field => {
      validateField(field, userInput[field]);
    });
  }

  $: getInputClass = (field: keyof typeof errors) => {
    return errorState[field] ? "error" : "";
  };

  let oidDebounceTimer: ReturnType<typeof setTimeout>;
  let currentUser: any;
  let apiError = '';

  onMount(() => {
    const unsubscribe = userStore.subscribe(store => {
      currentUser = store.user;
    });

    return () => {
      if (oidDebounceTimer) {
        clearTimeout(oidDebounceTimer);
      }
      unsubscribe();
    };
  });

  function isValidUUID(uuid: string) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  function resetUserState() {
    userExists = false;
    apiError = '';
    userInput = {
      ...userInput,
      name: '',
      email: '',
      user_type: '',
      user_role: ''
    };
    // Validate all fields after reset
    validateField('user_type', '');
    validateField('user_role', '');
  }

  async function debouncedOidChange(value: string) {
    clearTimeout(oidDebounceTimer);
    resetUserState();
    validateField('oid', value);
    
    // Step 1: Validate UUID format
    if (!value || !isValidUUID(value)) {
      return;
    }

    oidDebounceTimer = setTimeout(async () => {
      if (!currentUser?.oid) return;

      try {
        // Step 2: Check if user exists in our database
        const existingUserData = await getUserByOID(value);
        if (existingUserData?.user?.user_id) {
          userExists = true;
          validateField('oid', value);
          return;
        }

        // Step 3: Check Azure AD
        const adResponse = await getUserFromAzureAD(value, currentUser.oid);
        if (!adResponse?.ADUser?.name) {
          apiError = 'User not found in Azure AD';
          userInput = { ...userInput, name: '', email: '' };
          validateField('oid', value);
          validateField('user_type', '');
          validateField('user_role', '');
          return;
        }

        // Step 4: Validate and show AD user info
        const adUser = adResponse.ADUser;
        userInput = {
          ...userInput,
          name: adUser.name || '',
          email: adUser.email || ''
        };

        // Step 5: Only proceed if we have valid name and email
        if (!adUser.name || !adUser.email) {
          apiError = 'User found in Azure AD, but is invalid';
          validateField('oid', value);
          validateField('user_type', '');
          validateField('user_role', '');
        } else {
          validateField('oid', value);
        }

        dispatch('oidChange', adResponse);
        dispatch('fieldChange', userInput);

      } catch (error) {
        apiError = 'User not found in Azure AD';
        userInput = { ...userInput, name: '', email: '' };
        validateField('oid', value);
        validateField('user_type', '');
        validateField('user_role', '');
        dispatch('fieldChange', userInput);
      }
    }, 250);
  }

  function saveChanges() {
    if (!hasErrors) {
      dispatch("save", userInput);
    }
  }

  function cancelEdit() {
    dispatch("close");
  }

  function handleUserTypeChange(event: Event) {
    const target = event.target as HTMLInputElement;
    userInput.user_type = target.value;
    userInput.user_role = "";
    validateField('user_type', target.value);
    validateField('user_role', '');
    dispatch('fieldChange', userInput);
  }

  function handleUserRoleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    userInput.user_role = target.value;
    validateField('user_role', target.value);
    dispatch('fieldChange', userInput);
  }

  $: selectedUserType = lookups.user_type.list.find(t => t.id === userInput.user_type);
  $: availableRoles = selectedUserType?.role.list || [];

  export function triggerSave() {
    saveChanges();
  }

  $: isEditMode = action === 'edit';
</script>

<Card class="app-mutation-area max-w-sm flex flex-col">
  <div>
    <div class="pl-2 pr-2">
      <div class="">
        <h4 class="card-label-text">
          OID {#if !userInput.name || !userInput.email || userExists || apiError}<FontAwesomeIcon icon={faAsterisk} class="ml-1 text-red-400" title="Required field" />{/if}
        </h4>
        {#if !userInput.oid || !isValidUUID(userInput.oid)}
          <Helper class="helper-text mb-1">Enter the OID from Azure AD to find the user.</Helper>
        {:else if userExists}
          <Helper class="helper-text error mb-1">User already exists in the system.</Helper>
        {:else if apiError}
          <Helper class="helper-text error mb-1">{apiError}</Helper>
        {:else if userInput.name && userInput.email}
          <Helper class="helper-text success mb-1">User found in Azure AD.</Helper>
        {:else}
          <Helper class="helper-text mb-1">Enter the OID from Azure AD to find the user.</Helper>
        {/if}
        <Input
          type="text"
          bind:value={userInput.oid}
          on:input={() => debouncedOidChange(userInput.oid)}
          class="app-field {getInputClass('oid')}"
          disabled={isEditMode}
          placeholder="Object ID ..."
        />
      </div>
    </div>

    <div class="pl-2 pr-2 mt-8">
      <h4 class="card-label-text">Name</h4>
      {#if apiError === 'User found in Azure AD, but is invalid' && (!userInput.name || userInput.name === 'unknown')}
        <Helper class="helper-text error mb-1">Invalid Name</Helper>
      {/if}
      <div class="app-field readonly">
        {userInput.name && userInput.name !== 'unknown' ? userInput.name : '...'}
      </div>
    </div>

    <div class="pl-2 pr-2">
      <h4 class="card-label-text mt-0">Email</h4>
      {#if apiError === 'User found in Azure AD, but is invalid' && !userInput.email}
        <Helper class="helper-text error mb-1">Invalid Email</Helper>
      {/if}
      <div class="app-field readonly">
        {userInput.email || '...'}
      </div>
    </div>

    {#if userInput.name && userInput.email && !userExists && !apiError}
      <div class="pl-2 pr-2 mt-4">
        <h4 class="card-label-text mb-2">
          User Type {#if errorState['user_type']}<FontAwesomeIcon icon={faAsterisk} class="ml-1 text-red-400" title="Required field" />{/if}
        </h4>
        <div class="space-y-2">
          {#each lookups.user_type.list as type}
            <div class="flex items-center">
              <Checkbox
                id="role-{type.id}"
                class="card-checkbox"
                value={type.id}
                checked={userInput.user_type === type.id}
                on:change={handleUserTypeChange}
              >{type.name}</Checkbox>
            </div>
          {/each}
        </div>
      </div>

      {#if selectedUserType}
        <div class="pl-2 pr-2 mt-4">
          <h4 class="card-label-text mb-2">
            Role {#if errorState['user_role']}<FontAwesomeIcon icon={faAsterisk} class="ml-1 text-red-400" title="Required field" />{/if}
          </h4>
          <div class="space-y-2">
            {#each availableRoles as role}
              <div class="flex items-center">
                <Checkbox
                  id="role-{role.id}"
                  class="card-checkbox"
                  value={role.id}
                  checked={userInput.user_role === role.id}
                  on:change={handleUserRoleChange}
                >{role.name}</Checkbox>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</Card>
